import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "../../context/TranslationContext";
import { useDispatch } from 'react-redux';
import { logout } from '../../redux/authSlice';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Country, State, City } from 'country-state-city';

import apiProvider from "../../apiProvider/api";
import { IMAGE_URL } from "../../network/apiClient";

const addressSchema = Yup.object().shape({
    label: Yup.string().required('Label is required'),
    name: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Name must contain only alphabets')
        .required('Name is required'),
    mobile: Yup.string()
        .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits')
        .required('Mobile is required'),
    streetAddress: Yup.string().required('Street Address is required'),
    city: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'City must contain only alphabets')
        .required('City is required'),
    country: Yup.string().required('Country is required'),
    state: Yup.string().required('State is required'),
    postalCode: Yup.string()
        .matches(/^\d{6}$/, 'Postal code must be exactly 6 digits')
        .required('Postal Code is required'),
});

const accountDetailsSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string()
        .matches(/^[0-9]+$/, 'Phone number must be only digits')
        .min(10, 'Too short')
        .max(12, 'Too long')
        .required('Phone number is required'),
});

const MyAccountPage = () => {
    const { translateSync } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('dashboard');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user?.name || 'Thennarasu Raja';
    const [addresses, setAddresses] = useState([]);
    const [userDetails, setUserDetails] = useState(null);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    // Address Modal State
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null); // Add this
    const [isEditMode, setIsEditMode] = useState(false);
    const [countries, setCountries] = useState(Country.getAllCountries());
    console.error("countries", countries)
    const [states, setStates] = useState([]);
    const [orders, setOrders] = useState([]);
    const [orderCounts, setOrderCounts] = useState({ totalCount: 0, activeCount: 0 });
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedOrderForCancel, setSelectedOrderForCancel] = useState(null);
    const [orderDetailsForCancel, setOrderDetailsForCancel] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [selectedItemsForCancel, setSelectedItemsForCancel] = useState([]);

    // Return Modal State
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedOrderForReturn, setSelectedOrderForReturn] = useState(null);
    const [orderDetailsForReturn, setOrderDetailsForReturn] = useState(null);
    const [returnReason, setReturnReason] = useState("");
    const [selectedItemsForReturn, setSelectedItemsForReturn] = useState([]);
    const [returnOrders, setReturnOrders] = useState([]);
    const [statusFilter, setStatusFilter] = useState('All');

    // Pagination State for Orders
    const [orderPage, setOrderPage] = useState(1);
    const [orderTotalPages, setOrderTotalPages] = useState(1);
    const [orderLimit] = useState(5);

    // Pagination State for Returns
    const [returnPage, setReturnPage] = useState(1);
    const [returnTotalPages, setReturnTotalPages] = useState(1);
    const [returnLimit] = useState(5);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchAddresses()
        fetchOrders(statusFilter);
        fetchOrderCounts();
        fetchUserDetails();
    }, []);

    useEffect(() => {
        fetchOrders(statusFilter, 1);
    }, [statusFilter]);

    const handleLogout = () => {
        dispatch(logout());
        localStorage.removeItem('userToken');
        toast.success("Logged out successfully!");
        setTimeout(() => {
            navigate("/login");
        }, 1500);
    };

    const handleCloseAddressModal = () => {
        setShowAddressModal(false);
        setEditingAddress(null);
        setIsEditMode(false);
    };
    const handleShowAddressModal = () => {
        setIsEditMode(false);
        setEditingAddress(null);
        setShowAddressModal(true);
    };

    const handleEditAddress = (address) => {
        setIsEditMode(true);
        setEditingAddress(address);

        // Set states based on country
        const country = countries.find(c => c.name === address.country);
        if (country) {
            const countryStates = State.getStatesOfCountry(country.isoCode);
            setStates(countryStates);
        }

        setShowAddressModal(true);
    };

    const handleDeleteAddress = async (addressId) => {
        try {
            const result = await apiProvider.deleteAddress(addressId);
            if (result && result.status) {
                toast.success("Address deleted successfully!");
                fetchAddresses();
            }
        } catch (error) {
            console.error("Error deleting address:", error);
            toast.error("Failed to delete address");
        }
    };

    const handleAddressSubmit = async (values, { resetForm }) => {
        try {
            // Get country name from isoCode
            const selectedCountry = countries.find(c => c.isoCode === values.country);
            const countryName = selectedCountry ? selectedCountry.name : '';

            // Get state name from isoCode
            const selectedState = states.find(s => s.isoCode === values.state);
            const stateName = selectedState ? selectedState.name : '';


            const payload = {
                userId: user?._id || user?.id,
                label: values.label.toUpperCase(), // WORK
                contactName: values.name,
                contactNumber: values.mobile.replace(/^0+/, ''), // remove leading 0
                addressLine: values.streetAddress,
                city: values.city,
                state: stateName,        // ✅ Store state NAME instead of code
                country: countryName,
                postalCode: values.postalCode
            };

            const result = await apiProvider.addAddress(payload);
            console.error("result :", result);
            if (result && result.status) {
                toast.success("Address added successfully!");
                resetForm();
                setShowAddressModal(false);
                // Optionally refresh address list here
            }
            fetchAddresses();
        } catch (error) {
            console.error("Error adding address:", error);
            //   toast.error("An error occurred.");
        }
    };

    const handleAddressUpdate = async (values, { resetForm }) => {
        try {
            // Get country name from isoCode
            const selectedCountry = countries.find(c => c.isoCode === values.country);
            const countryName = selectedCountry ? selectedCountry.name : '';

            // Get state name from isoCode
            const selectedState = states.find(s => s.isoCode === values.state);
            const stateName = selectedState ? selectedState.name : '';

            const payload = {
                userId: user?._id || user?.id,
                label: values.label.toUpperCase(), // WORK
                contactName: values.name,
                contactNumber: values.mobile.replace(/^0+/, ''), // remove leading 0
                addressLine: values.streetAddress,
                city: values.city,
                state: stateName,        // ✅ Store state NAME instead of code
                country: countryName,
                postalCode: values.postalCode
            };

            const result = await apiProvider.updateAddress(values.id, payload);
            console.error("result :", result);
            if (result && result.status) {
                toast.success("Address updated successfully!");
                resetForm();
                setShowAddressModal(false);
                // Optionally refresh address list here
            }
            fetchAddresses();
        } catch (error) {
            console.error("Error updating address:", error);
            //   toast.error("An error occurred.");
        }
    };

    const handleAccountUpdate = async (values) => {
        try {
            console.error("values :", values);
            const result = await apiProvider.updateUserDetails(user?._id, values);
            if (result && result.status) {
                toast.success("Profile updated successfully!");
                fetchUserDetails();
            }

        } catch (error) {
            console.error("Error updating account details:", error);
        }
    };

    const fetchAddresses = async () => {
        try {
            const result = await apiProvider.getAddress(user?._id, 'customer');

            if (result && result.status) {
                setAddresses(result.response?.data);
            }
        } catch (error) {
            console.error("Error fetching addresses:", error);
        }
    }

    const fetchUserDetails = async () => {
        try {
            const result = await apiProvider.getUserDetails(user?._id);

            if (result && result.status) {
                setUserDetails(result.response?.data);
            }
            console.error("userDetails :", result.response?.data);
        } catch (error) {
            console.error("Error fetching addresses:", error);
        }
    }

    const fetchOrders = async (status, page = 1) => {
        if (status === 'return') {
            const result = await apiProvider.getReturnOrders(user?._id || user?.id, page - 1, returnLimit);
            console.error("fetchReturnOrders result:", result);
            if (result.status) {
                setReturnOrders(result.response?.data || []);
                setReturnTotalPages(result.response?.totalPages || 1);
                setReturnPage(page);
            }
        } else {
            const result = await apiProvider.getOrders(status, page - 1, orderLimit);
            console.error("fetchOrders result:", result);
            if (result.status) {
                setOrders(result.response?.data);
                setOrderTotalPages(result.response?.totalPages || 1);
                setOrderPage(page);
            }
        }
    };

    const fetchOrderCounts = async () => {
        try {
            const result = await apiProvider.getOrderCounts();
            console.error("fetchOrderCounts result:", result);
            if (result && result.status) {
                setOrderCounts({
                    totalCount: result.response?.data?.totalCount || 0,
                    activeCount: result.response?.data?.activeCount || 0
                });
            }
        } catch (error) {
            console.error("Error fetching order counts:", error);
        }
    };

    const handleCancelClick = async (order) => {
        try {
            const result = await apiProvider.orderDetails(order._id);
            if (result && result.status) {
                setSelectedOrderForCancel(order);
                setOrderDetailsForCancel(result.response?.data);

                // By default, unselect all items
                setSelectedItemsForCancel([]);

                setCancelReason("");
                setShowCancelModal(true);
            } else {
                toast.error("Failed to fetch order details");
            }
        } catch (error) {
            console.error("Error fetching order details for cancel:", error);
            toast.error("An error occurred");
        }
    };

    const handleCancelSubmit = async () => {
        if (!cancelReason.trim()) {
            toast.error("Cancellation reason is mandatory");
            return;
        }

        if (selectedItemsForCancel.length === 0) {
            toast.error("Please select at least one item to cancel");
            return;
        }

        console.log("selectedItemsForCancel :", selectedItemsForCancel);
        try {
            // Construct the cancelledItems array as per backend requirements
            const cancelledItems = selectedItemsForCancel.map(idx => {
                const item = orderDetailsForCancel?.items?.[idx];
                return {
                    productId: item?.productId,
                    attributes: item?.attributes || {}
                };
            });

            const payload = {
                orderId: selectedOrderForCancel._id,
                userId: user?._id || user?.id,
                reason: cancelReason,
                cancelledItems: cancelledItems
            };

            console.log("payload :", payload);

            const result = await apiProvider.updateOrderStatus(payload);
            if (result && result.status) {
                toast.success("Cancellation request submitted successfully");
                setShowCancelModal(false);
                fetchOrders();
                fetchOrderCounts();
            } else {
                // Directly handle the backend error response message
                const apiErrorMessage = result?.response?.message || "Failed to submit cancellation request";
                toast.error(apiErrorMessage);
            }
        } catch (error) {
            console.error("Error submitting cancellation request:", error);
            toast.error("An error occurred during submission");
        }
    };

    const handleReturnClick = async (order) => {
        try {
            const result = await apiProvider.orderDetails(order._id);
            if (result && result.status) {
                setSelectedOrderForReturn(order);
                setOrderDetailsForReturn(result.response?.data);

                // By default, unselect all items
                setSelectedItemsForReturn([]);

                setReturnReason("");
                setShowReturnModal(true);
            } else {
                toast.error("Failed to fetch order details");
            }
        } catch (error) {
            console.error("Error fetching order details for return:", error);
            toast.error("An error occurred");
        }
    };

    const handleReturnSubmit = async () => {
        if (!returnReason.trim()) {
            toast.error("Return reason is mandatory");
            return;
        }

        if (selectedItemsForReturn.length === 0) {
            toast.error("Please select at least one item to return");
            return;
        }

        try {
            // Construct the items array for the return request
            const items = selectedItemsForReturn.map(idx => {
                const item = orderDetailsForReturn?.items?.[idx];
                return {
                    productId: item?.productId,
                    attributes: item?.attributes || {}
                };
            });

            const payload = {
                orderId: selectedOrderForReturn._id,
                placedBy: user?._id || user?.id,
                placedByModel: "User",
                items: items,
                reason: returnReason
            };

            console.log("Returned payload:", payload);

            const result = await apiProvider.createReturnOrder(payload);
            if (result && result.status) {
                toast.success("Return request submitted successfully");
                setShowReturnModal(false);
                fetchOrders();
                fetchOrderCounts();
            } else {
                const apiErrorMessage = result?.response?.message || "Failed to submit return request";
                toast.error(apiErrorMessage);
            }

        } catch (error) {
            console.error("Error submitting return request:", error);
            toast.error("An error occurred during submission");
        }
    };

    const toggleItemSelectionForReturn = (idx) => {
        setSelectedItemsForReturn(prev =>
            prev.includes(idx)
                ? prev.filter(i => i !== idx)
                : [...prev, idx]
        );
    };

    const handleSelectAllForReturn = () => {
        if (selectedItemsForReturn.length === orderDetailsForReturn?.products?.length) {
            setSelectedItemsForReturn([]);
        } else {
            const allIndexes = orderDetailsForReturn?.products?.map((_, i) => i) || [];
            setSelectedItemsForReturn(allIndexes);
        }
    };

    const toggleItemSelection = (idx) => {
        setSelectedItemsForCancel(prev =>
            prev.includes(idx)
                ? prev.filter(i => i !== idx)
                : [...prev, idx]
        );
    };

    const handleSelectAll = () => {
        if (selectedItemsForCancel.length === orderDetailsForCancel?.products?.length) {
            setSelectedItemsForCancel([]);
        } else {
            const allIndexes = orderDetailsForCancel?.products?.map((_, i) => i) || [];
            setSelectedItemsForCancel(allIndexes);
        }
    };

    // Dashboard Content
    const renderDashboard = () => (
        <div className="dashboard-content">
            <div className="welcome-section mb-4 pb-3 border-bottom">
                <h2 className="fw-bold mb-2" style={{ color: '#3a3530' }}>
                    Hello {userDetails?.name || userName}!
                </h2>
                <p className="text-muted fs-6 mb-0">
                    Welcome back to your account dashboard
                </p>
            </div>

            <div className="dashboard-description bg-light p-4 rounded-3 mb-4">
                <p className="fs-5 mb-0">
                    From your account dashboard, you can easily check & view your{" "}
                    <span className="fw-semibold" style={{ color: '#582d86' }}>
                        recent orders
                    </span>
                    , manage your{" "}
                    <span className="fw-semibold" style={{ color: '#582d86' }}>
                        shipping and billing addresses
                    </span>{" "}
                    and edit your{" "}
                    <span className="fw-semibold" style={{ color: '#582d86' }}>
                        password and account details
                    </span>
                    .
                </p>
            </div>

            <div className="row g-4 mt-2">
                <div className="col-md-6">
                    <div className="stat-card bg-white p-4 rounded-3 shadow-sm">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted mb-2">Total Orders</h6>
                                <h3 className="fw-bold mb-0">{orderCounts.totalCount}</h3>
                                <small className="text-success">
                                    <i className="bi bi-arrow-up me-1"></i>Last 30 days
                                </small>
                            </div>
                            <div className="stat-icon p-3 rounded-circle" style={{ backgroundColor: '#582d8620' }}>
                                <i className="bi bi-cart-check fs-3" style={{ color: '#582d86' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="stat-card bg-white p-4 rounded-3 shadow-sm">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted mb-2">Active Orders</h6>
                                <h3 className="fw-bold mb-0">{orderCounts.activeCount}</h3>
                                <small className="text-warning">
                                    <i className="bi bi-clock me-1"></i>In progress
                                </small>
                            </div>
                            <div className="stat-icon bg-warning bg-opacity-10 p-3 rounded-circle">
                                <i className="bi bi-truck fs-3 text-warning"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Orders Content
    const renderOrders = () => {
        const orderStatuses = ['All', 'pending', 'shipped', 'delivered', 'cancelled', 'packed', 'return'];

        return (
            <div className="orders-content">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <h3 className="fw-bold mb-0">My Orders</h3>
                    <div className="d-flex align-items-center">
                        <label className="me-2 fw-medium text-muted mb-0">Filter:</label>
                        <select
                            className="form-select form-select-sm shadow-sm"
                            style={{ width: '105px', borderRadius: '8px', borderColor: '#582d8620' }}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            {orderStatuses.map(status => (
                                <option key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="table-responsive bg-white rounded-3 shadow-sm">
                    {statusFilter === 'return' ? (
                        <table className="table table-hover mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="px-4 py-3">Return ID</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Reason</th>
                                    <th className="px-4 py-3">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {returnOrders && returnOrders.length > 0 ? (
                                    returnOrders.map((order) => (
                                        <tr key={order._id}>
                                            <td className="px-4 py-3 fw-medium">#{order.orderCode}</td>
                                            <td className="px-4 py-3 text-muted">
                                                {new Date(order.createdAt).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill text-capitalize" style={{ color: '#582d86', backgroundColor: '#582d8620' }}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted">
                                                <small>{order.reason}</small>
                                            </td>
                                            <td className="px-4 py-3 fw-medium">{order.currency === 'SGD' ? 'S$' : '₹'}{order.totalAmount}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5">
                                            <div className="text-muted">
                                                <i className="bi bi-box-seam fs-1 d-block mb-3 opacity-25"></i>
                                                No return orders found yet.
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="table table-hover mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="px-4 py-3">Order ID</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Payment</th>
                                    <th className="px-4 py-3">Total</th>
                                    <th className="px-4 py-3">View</th>
                                    <th className="px-4 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders && orders.length > 0 ? (
                                    orders.map((order) => (
                                        <tr key={order._id}>
                                            <td className="px-4 py-3 fw-medium">#{order.orderCode}</td>
                                            <td className="px-4 py-3 text-muted">
                                                {new Date(order.createdAt).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`badge ${order.status === 'delivered' ? 'bg-success' :
                                                    order.status === 'pending' ? 'bg-warning' :
                                                        order.status === 'cancelled' ? 'bg-danger' :
                                                            'bg-primary'
                                                    } bg-opacity-10 ${order.status === 'delivered' ? 'text-success' :
                                                        order.status === 'pending' ? 'text-warning' :
                                                            order.status === 'cancelled' ? 'text-danger' :
                                                                'text-primary'
                                                    } px-3 py-2 rounded-pill text-capitalize`} style={order.status !== 'delivered' && order.status !== 'pending' && order.status !== 'cancelled' ? { color: '#582d86', backgroundColor: '#582d8620' } : {}}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted">
                                                <small>{order.paymentMode}</small>
                                                <div style={{ fontSize: '10px' }}>
                                                    {order.paymentStatus === 'pending' ?
                                                        <span className="text-warning">● Pending</span> :
                                                        <span className="text-success">● Paid</span>
                                                    }
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 fw-medium">{order.currency === 'SGD' ? 'S$' : '₹'}{order.totalAmount}</td>
                                            <td className="px-4 py-3">
                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        color: '#582d86',
                                                        border: '1px solid #582d86',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.backgroundColor = '#582d86';
                                                        e.target.style.color = '#ffffff';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.backgroundColor = 'transparent';
                                                        e.target.style.color = '#582d86';
                                                    }}
                                                    onClick={() => navigate(`/invoice/${order._id}`)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                {order.status === 'delivered' ? (
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => handleReturnClick(order)}
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            color: '#1e293b',
                                                            border: '1px solid #1e293b',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.target.style.backgroundColor = '#1e293b';
                                                            e.target.style.color = '#ffffff';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.target.style.backgroundColor = 'transparent';
                                                            e.target.style.color = '#1e293b';
                                                        }}
                                                    >
                                                        Return
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleCancelClick(order)}
                                                        disabled={order.status?.toLowerCase() === 'cancelled' || order.status?.toLowerCase() === 'return-initiated'}
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="text-muted">
                                                <i className="bi bi-box-seam fs-1 d-block mb-3 opacity-25"></i>
                                                No orders found yet.
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination UI */}
                {console.log("Pagination State:", { statusFilter, orderTotalPages, returnTotalPages, orderPage, returnPage })}
                {(statusFilter === 'return' ? returnTotalPages : orderTotalPages) >= 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <nav aria-label="Page navigation">
                            <ul className="pagination pagination-sm">
                                <li className={`page-item ${(statusFilter === 'return' ? returnPage : orderPage) === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => fetchOrders(statusFilter, (statusFilter === 'return' ? returnPage : orderPage) - 1)}
                                        aria-label="Previous"
                                    >
                                        <span aria-hidden="true">&laquo;</span>
                                    </button>
                                </li>
                                {[...Array(statusFilter === 'return' ? returnTotalPages : orderTotalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${(statusFilter === 'return' ? returnPage : orderPage) === i + 1 ? 'active' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => fetchOrders(statusFilter, i + 1)}
                                            style={(statusFilter === 'return' ? returnPage : orderPage) === i + 1 ? { backgroundColor: '#582d86', borderColor: '#582d86' } : { color: '#582d86' }}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${(statusFilter === 'return' ? returnPage : orderPage) === (statusFilter === 'return' ? returnTotalPages : orderTotalPages) ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => fetchOrders(statusFilter, (statusFilter === 'return' ? returnPage : orderPage) + 1)}
                                        aria-label="Next"
                                    >
                                        <span aria-hidden="true">&raquo;</span>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>
        );
    };

    // Returns Content
    const renderReturns = () => (
        <div className="returns-content">
            <h3 className="fw-bold mb-4 pb-2 border-bottom">Returns & Refunds</h3>
            <div className="bg-light p-4 rounded-3 mb-4">
                <p className="mb-0">You have no active return requests.</p>
            </div>
            <div className="bg-white p-4 rounded-3 shadow-sm">
                <h5 className="fw-bold mb-3">Request a Return</h5>
                <form>
                    <div className="mb-3">
                        <label className="form-label fw-medium">Order ID</label>
                        <input type="text" className="form-control" placeholder="Enter your order ID" />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-medium">Reason for Return</label>
                        <select className="form-select">
                            <option>Select a reason</option>
                            <option>Damaged product</option>
                            <option>Wrong item received</option>
                            <option>Not satisfied</option>
                            <option>Other</option>
                        </select>
                    </div>
                    <button className="btn text-white" style={{ backgroundColor: '#582d86' }}>Submit Request</button>
                </form>
            </div>
        </div>
    );

    // Track Your Order Content
    const renderTrackOrder = () => (
        <div className="track-order-content">
            <h3 className="fw-bold mb-4 pb-2 border-bottom">Track Your Order</h3>
            <div className="bg-white p-5 rounded-3 shadow-sm text-center">
                <div className="mb-4">
                    <i className="bi bi-truck fs-1 text-primary opacity-50"></i>
                </div>
                <p className="text-muted mb-4">
                    To track your order please enter your Order ID in the box below and press "Track" button.
                    This was given to you on your receipt and in the confirmation email you should have received.
                </p>
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="input-group mb-3">
                            <span className="input-group-text bg-light">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Order ID (e.g. ORD-001)"
                            />
                            <button className="btn text-white px-4" style={{ backgroundColor: '#582d86' }}>Track</button>
                        </div>
                        <small className="text-muted">
                            Found in your order confirmation email
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );

    // My Address Content
    const renderAddress = () => (
        <div className="address-content">
            <h3 className="fw-bold mb-4 pb-2 border-bottom">My Address</h3>
            <div className="row g-4">
                {addresses.length === 0 ? (
                    <p className="text-muted">No addresses found.</p>
                ) : (
                    addresses.map((address, index) => (
                        <div className="col-md-6" key={address._id}>
                            <div className="bg-white p-4 rounded-3 shadow-sm h-100">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">
                                        <i className="bi bi-map me-2" style={{ color: '#582d86' }}></i>
                                        Address #{index + 1} ({address.label})
                                    </h5>

                                    {address.isDefault && (
                                        <span className="badge" style={{ backgroundColor: '#582d86' }}>Default</span>
                                    )}
                                </div>

                                <p className="mb-1 fw-bold">{address.contactName}</p>
                                <p className="mb-1 text-muted">{address.addressLine}</p>
                                <p className="mb-1 text-muted">
                                    {address.city}, {address.state} {address.postalCode}
                                </p>
                                <p className="mb-3 text-muted">{address.country}</p>

                                <p className="mb-0">
                                    <i className="bi bi-telephone me-2"></i>
                                    +91 {address.contactNumber}
                                </p>

                                <div className="mt-3">
                                    <button
                                        className="btn btn-sm btn-outline-primary me-2"
                                        style={{ color: '#582d86', borderColor: '#582d86' }}
                                        onClick={() => handleEditAddress(address)}
                                    >
                                        Edit
                                    </button>
                                    <button className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDeleteAddress(address._id)}>
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="mt-4">
                <button className="btn text-white" style={{ backgroundColor: '#582d86' }} onClick={handleShowAddressModal}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Add New Address
                </button>
            </div>
        </div>
    );

    // Account Details Content
    const renderAccountDetails = () => (
        <div className="account-details-content">
            <div className="mb-4 pb-1 border-bottom d-flex justify-content-between align-items-center">
                <h3 className="fw-bold mb-0" style={{ fontFamily: "'Playfair Display', serif" }}>Account Details</h3>
            </div>
            <div className="bg-white p-4 rounded-0 shadow-sm border border-light">
                <Formik
                    enableReinitialize
                    initialValues={{
                        name: userDetails?.name || user?.name || "",
                        email: userDetails?.email || user?.email || "",
                        phone: userDetails?.phone || user?.phone || "",
                    }}
                    validationSchema={accountDetailsSchema}
                    onSubmit={handleAccountUpdate}
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                        <Form>
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div className="form-group mb-0">
                                        <label className="small text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Full Name</label>
                                        <div className="input-with-icon">
                                            <i className="bi bi-person text-muted"></i>
                                            <input
                                                type="text"
                                                name="name"
                                                className={`form-control boutique-input ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter your name"
                                            />
                                        </div>
                                        {touched.name && errors.name && <div className="invalid-feedback d-block mt-1 small text-start">{errors.name}</div>}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group mb-0">
                                        <label className="small text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Phone Number</label>
                                        <div className="input-with-icon">
                                            <i className="bi bi-phone text-muted"></i>
                                            <input
                                                type="tel"
                                                name="phone"
                                                className={`form-control boutique-input ${touched.phone && errors.phone ? 'is-invalid' : ''}`}
                                                value={values.phone}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                                    setFieldValue('phone', val);
                                                }}
                                                onBlur={handleBlur}
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                        {touched.phone && errors.phone && <div className="invalid-feedback d-block mt-1 small text-start">{errors.phone}</div>}
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-group mb-0">
                                        <label className="small text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Email Address</label>
                                        <div className="input-with-icon">
                                            <i className="bi bi-envelope text-muted"></i>
                                            <input
                                                type="email"
                                                name="email"
                                                className={`form-control boutique-input ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter email address"
                                            />
                                        </div>
                                        {touched.email && errors.email && <div className="invalid-feedback d-block mt-1 small text-start">{errors.email}</div>}
                                    </div>
                                </div>
                            </div>

                             <div className="mt-5 text-center">
                                <button type="submit" className="btn btn-dark px-5 py-3 text-uppercase fw-bold rounded-0" style={{ letterSpacing: '2px', fontSize: '14px' }}>
                                    Save Changes
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>My Account | Colours Boutique</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <section className="myaccount-page-wrapper py-5 bg-light min-vh-100 d-flex align-items-start">
                <div className="container">
                    <div className="row g-4">
                        {/* LEFT SIDEBAR - FULL HEIGHT */}
                        <div className="col-md-4 col-lg-3">
                            <div className="sticky-sidebar" style={{ position: 'sticky', top: '100px' }}>
                                <div className="user-profile-card bg-white p-3 rounded-3 shadow-sm mb-2">
                                    <div className="d-flex align-items-center gap-4">
                                        <div className="profile-icon bg-primary bg-opacity-10 p-3 rounded-circle">
                                            <i className="bi bi-person-circle fs-3 text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-1">{userDetails?.name || userName}</h6>
                                            <small className="text-muted">{userDetails?.email || user?.email}</small>
                                        </div>
                                    </div>
                                </div>

                                <div className="list-group shadow-sm rounded-3 overflow-hidden">
                                    <button
                                        className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${activeTab === 'dashboard' ? 'active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab('dashboard')}
                                    >
                                        <i className={`bi bi-sliders me-3 fs-5 ${activeTab === 'dashboard' ? 'text-white' : 'text-primary'}`}></i>
                                        Dashboard
                                    </button>

                                    <button
                                        className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${activeTab === 'orders' ? 'active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab('orders')}
                                    >
                                        <i className={`bi bi-bag me-3 fs-5 ${activeTab === 'orders' ? 'text-white' : 'text-primary'}`}></i>
                                        Orders
                                    </button>

                                    {/* <button
                                        className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${activeTab === 'returns' ? 'active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab('returns')}
                                    >
                                        <i className={`bi bi-arrow-return-left me-3 fs-5 ${activeTab === 'returns' ? 'text-white' : 'text-primary'}`}></i>
                                        Returns
                                    </button> */}

                                    {/* <button
                                        className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${activeTab === 'track' ? 'active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab('track')}
                                    >
                                        <i className={`bi bi-truck me-3 fs-5 ${activeTab === 'track' ? 'text-white' : 'text-primary'}`}></i>
                                        Track Your Order
                                    </button> */}

                                    <button
                                        className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${activeTab === 'address' ? 'active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab('address')}
                                    >
                                        <i className={`bi bi-geo-alt me-3 fs-5 ${activeTab === 'address' ? 'text-white' : 'text-primary'}`}></i>
                                        My Address
                                    </button>

                                    <button
                                        className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${activeTab === 'account' ? 'active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab('account')}
                                    >
                                        <i className={`bi bi-person me-3 fs-5 ${activeTab === 'account' ? 'text-white' : 'text-primary'}`}></i>
                                        Account Details
                                    </button>

                                    <button
                                        className="list-group-item list-group-item-action d-flex align-items-center py-3 text-danger"
                                        onClick={handleLogout}
                                    >
                                        <i className="bi bi-box-arrow-right me-3 fs-5 text-danger"></i>
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT CONTENT - DYNAMIC BASED ON ACTIVE TAB */}
                        <div className="col-md-8 col-lg-9">
                            <div className="bg-white p-4 p-lg-5 shadow-sm rounded-3 min-vh-75">
                                {activeTab === 'dashboard' && renderDashboard()}
                                {activeTab === 'orders' && renderOrders()}
                                {activeTab === 'returns' && renderReturns()}
                                {activeTab === 'track' && renderTrackOrder()}
                                {activeTab === 'address' && renderAddress()}
                                {activeTab === 'account' && renderAccountDetails()}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Order Cancellation Modal - Refined Redesign */}
            {showCancelModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '800px' }}>
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '8px' }}>
                            <div className="modal-header border-bottom-0 pt-4 px-4 pb-0">
                                <h4 className="modal-title fw-bold" style={{ color: '#1a2b4b', fontSize: '24px' }}>Add a cancel request</h4>
                                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body px-4 pt-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Click on a row to select the item</p>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSelectAll}
                                        style={{ fontSize: '12px' }}
                                    >
                                        {selectedItemsForCancel.length === orderDetailsForCancel?.products?.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="table-responsive border rounded">
                                    <table className="table mb-0 text-center align-middle" style={{ minWidth: '600px' }}>
                                        <thead style={{ backgroundColor: '#fcfcfc' }}>
                                            <tr style={{ color: '#6c757d', fontSize: '14px', borderBottom: '1px solid #dee2e6' }}>
                                                <th className="py-3 fw-bold text-center" style={{ width: '60px' }}>#</th>
                                                <th className="py-3 fw-bold">Image</th>
                                                <th className="py-3 fw-bold text-start">Product name</th>
                                                <th className="py-3 fw-bold">Product Price</th>
                                                <th className="py-3 fw-bold">Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ fontSize: '14px' }}>
                                            {orderDetailsForCancel?.products?.map((product, idx) => {
                                                const orderItem = orderDetailsForCancel?.items?.[idx];
                                                const isSelected = selectedItemsForCancel.includes(idx);
                                                return (
                                                    <tr key={`${product._id}_${idx}`}
                                                        onClick={() => toggleItemSelection(idx)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: isSelected ? '#fef2f2' : 'transparent',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                    >
                                                        <td className="text-center">
                                                            {isSelected ? (
                                                                <i className="bi bi-check-circle-fill" style={{ color: '#cc0000', fontSize: '20px' }}></i>
                                                            ) : (
                                                                <i className="bi bi-circle" style={{ color: '#ced4da', fontSize: '20px' }}></i>
                                                            )}
                                                        </td>
                                                        <td className="py-3">
                                                            <img
                                                                src={(() => {
                                                                    const matchingRow = product?.customerAttribute?.rowData?.find(row =>
                                                                        Object.entries(orderItem?.attributes || {}).every(
                                                                            ([key, value]) => {
                                                                                const rowKey = Object.keys(row).find(k => {
                                                                                    const kLower = k.toLowerCase();
                                                                                    const keyLower = key.toLowerCase();
                                                                                    if (kLower === keyLower) return true;
                                                                                    if ((kLower === 'color' || kLower === 'colour') && (keyLower === 'color' || keyLower === 'colour')) return true;
                                                                                    return false;
                                                                                });
                                                                                return rowKey && String(row[rowKey]) === String(value);
                                                                            }
                                                                        )
                                                                    );
                                                                    const imgData = matchingRow?.images?.[0] || product?.productImage?.[0] || product?.additionalImage?.[0];
                                                                    return imgData ? `${IMAGE_URL}${imgData.docPath}/${imgData.docName}` : '/img/products/default.png';
                                                                })()}
                                                                alt={product.productName}
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                                            />
                                                        </td>
                                                        <td className="text-start">
                                                            <div className="fw-bold text-dark text-uppercase mb-1" style={{ fontSize: '13px', letterSpacing: '0.3px' }}>
                                                                {product.productName}
                                                            </div>
                                                            <div className="text-muted text-capitalize" style={{ fontSize: '12px' }}>
                                                                {product.attributeData?.map((attr, aIdx) => (
                                                                    <span key={attr._id}>
                                                                        {attr.name}: <span className="text-dark">{attr.value?.[0]?.value || 'N/A'}</span>
                                                                        {aIdx < product.attributeData.length - 1 ? ', ' : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="text-dark">{orderDetailsForCancel?.currency === 'SGD' ? 'S$' : '₹'}{orderItem?.unitPrice || product.price}</td>
                                                        <td className="text-dark">{orderItem?.quantity || 1}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-4">
                                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '15px' }}>Enter your reason to cancel your product</h6>
                                    <textarea
                                        className="form-control border shadow-none"
                                        rows="2"
                                        placeholder="Reason"
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        style={{ fontSize: '14px', resize: 'none', borderRadius: '6px', padding: '12px' }}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer border-top-0 px-4 pb-4 justify-content-center">
                                <button
                                    type="button"
                                    className="btn btn-danger fw-bold border-0"
                                    onClick={handleCancelSubmit}
                                    style={{
                                        backgroundColor: '#cc0000',
                                        padding: '12px 30px',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#b30000'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#cc0000'}
                                >
                                    Submit request
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Order Return Modal */}
            {showReturnModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '800px' }}>
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '8px' }}>
                            <div className="modal-header border-bottom-0 pt-4 px-4 pb-0">
                                <h4 className="modal-title fw-bold" style={{ color: '#1a2b4b', fontSize: '24px' }}>Add a return request</h4>
                                <button type="button" className="btn-close" onClick={() => setShowReturnModal(false)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body px-4 pt-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Click on a row to select the item</p>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSelectAllForReturn}
                                        style={{ fontSize: '12px' }}
                                    >
                                        {selectedItemsForReturn.length === orderDetailsForReturn?.products?.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="table-responsive border rounded">
                                    <table className="table mb-0 text-center align-middle" style={{ minWidth: '600px' }}>
                                        <thead style={{ backgroundColor: '#fcfcfc' }}>
                                            <tr style={{ color: '#6c757d', fontSize: '14px', borderBottom: '1px solid #dee2e6' }}>
                                                <th className="py-3 fw-bold text-center" style={{ width: '60px' }}>#</th>
                                                <th className="py-3 fw-bold">Image</th>
                                                <th className="py-3 fw-bold text-start">Product name</th>
                                                <th className="py-3 fw-bold">Product Price</th>
                                                <th className="py-3 fw-bold">Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ fontSize: '14px' }}>
                                            {orderDetailsForReturn?.products?.map((product, idx) => {
                                                const orderItem = orderDetailsForReturn?.items?.[idx];
                                                const isSelected = selectedItemsForReturn.includes(idx);
                                                return (
                                                    <tr key={`${product._id}_${idx}`}
                                                        onClick={() => toggleItemSelectionForReturn(idx)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: isSelected ? '#f1f5f9' : 'transparent',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                    >
                                                        <td className="text-center">
                                                            {isSelected ? (
                                                                <i className="bi bi-check-circle-fill" style={{ color: '#1e293b', fontSize: '20px' }}></i>
                                                            ) : (
                                                                <i className="bi bi-circle" style={{ color: '#ced4da', fontSize: '20px' }}></i>
                                                            )}
                                                        </td>
                                                        <td className="py-3">
                                                            <img
                                                                src={(() => {
                                                                    const returnMatchingRow = product?.customerAttribute?.rowData?.find(row =>
                                                                        Object.entries(orderItem?.attributes || {}).every(
                                                                            ([key, value]) => {
                                                                                const rowKey = Object.keys(row).find(k => {
                                                                                    const kLower = k.toLowerCase();
                                                                                    const keyLower = key.toLowerCase();
                                                                                    if (kLower === keyLower) return true;
                                                                                    if ((kLower === 'color' || kLower === 'colour') && (keyLower === 'color' || keyLower === 'colour')) return true;
                                                                                    return false;
                                                                                });
                                                                                return rowKey && String(row[rowKey]) === String(value);
                                                                            }
                                                                        )
                                                                    );
                                                                    const imgData = returnMatchingRow?.images?.[0] || product?.productImage?.[0] || product?.additionalImage?.[0];
                                                                    return imgData ? `${IMAGE_URL}${imgData.docPath}/${imgData.docName}` : '/img/products/default.png';
                                                                })()}
                                                                alt={product.productName}
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                                            />
                                                        </td>
                                                        <td className="text-start">
                                                            <div className="fw-bold text-dark text-uppercase mb-1" style={{ fontSize: '13px', letterSpacing: '0.3px' }}>
                                                                {product.productName}
                                                            </div>
                                                            <div className="text-muted text-capitalize" style={{ fontSize: '12px' }}>
                                                                {product.attributeData?.map((attr, aIdx) => (
                                                                    <span key={attr._id}>
                                                                        {attr.name}: <span className="text-dark">{attr.value?.[0]?.value || 'N/A'}</span>
                                                                        {aIdx < product.attributeData.length - 1 ? ', ' : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="text-dark">{orderDetailsForReturn?.currency === 'SGD' ? 'S$' : '₹'}{orderItem?.unitPrice || product.price}</td>
                                                        <td className="text-dark">{orderItem?.quantity || 1}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-4">
                                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '15px' }}>Enter your reason to return your product</h6>
                                    <textarea
                                        className="form-control shadow-none"
                                        rows="2"
                                        placeholder="Please detail the reason for your return..."
                                        value={returnReason}
                                        onChange={(e) => setReturnReason(e.target.value)}
                                        style={{
                                            backgroundColor: '#f8f9fc',
                                            border: '1px solid #e2e8f0',
                                            fontSize: '14px',
                                            resize: 'none',
                                            borderRadius: '8px',
                                            padding: '14px'
                                        }}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer border-top-0 px-4 pb-4 justify-content-center">
                                <button
                                    type="button"
                                    className="btn fw-bold border-0 text-white"
                                    onClick={handleReturnSubmit}
                                    style={{
                                        backgroundColor: '#1e293b',
                                        padding: '12px 30px',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#334155'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#1e293b'}
                                >
                                    Submit request
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Address Modal */}
            {showAddressModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '700px' }}>
                        <div className="modal-content">
                            <div className="modal-header border-bottom-0 pb-0">
                                <h5 className="modal-title fw-bold">
                                    {isEditMode ? 'Edit Address' : 'Add Address'}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCloseAddressModal}></button>
                            </div>
                            <div className="modal-body">
                                <Formik
                                    enableReinitialize
                                    initialValues={{
                                        id: editingAddress?._id || '',
                                        label: editingAddress?.label || '',
                                        name: editingAddress?.contactName || '',
                                        mobile: editingAddress?.contactNumber || '',
                                        streetAddress: editingAddress?.addressLine || '',
                                        city: editingAddress?.city || '',
                                        country: editingAddress?.country
                                            ? countries.find(c => c.name === editingAddress.country)?.isoCode || ''
                                            : '',
                                        state: editingAddress?.state
                                            ? states.find(s => s.name === editingAddress.state)?.isoCode || ''
                                            : '',
                                        postalCode: editingAddress?.postalCode || ''
                                    }}
                                    validationSchema={addressSchema}
                                    onSubmit={isEditMode ? handleAddressUpdate : handleAddressSubmit}
                                >
                                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                                        <Form>
                                            <div className="row g-3">
                                                <Field type="hidden" name="id" />
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <Field
                                                            as="select"
                                                            name="label"
                                                            className={`form-select ${touched.label && errors.label ? 'is-invalid' : ''}`}
                                                            style={{ border: '1.5px solid #dee2e6', borderRadius: '8px' }}
                                                        >
                                                            <option value=""></option>
                                                            <option value="Home">Home</option>
                                                            <option value="Work">Work</option>
                                                            <option value="Other">Other</option>
                                                        </Field>

                                                        <label>Label *</label>

                                                        {touched.label && errors.label && (
                                                            <div className="invalid-feedback text-start">{errors.label}</div>
                                                        )}
                                                    </div>

                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <Field
                                                            name="name"
                                                            className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                                            placeholder="Name"
                                                            onKeyPress={(event) => {
                                                                if (!/[a-zA-Z\s]/.test(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <label>Name *</label>
                                                        {touched.name && errors.name && <div className="invalid-feedback text-start">{errors.name}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <Field
                                                            name="mobile"
                                                            className={`form-control ${touched.mobile && errors.mobile ? 'is-invalid' : ''}`}
                                                            placeholder="Mobile"
                                                            maxLength={10}
                                                            onKeyPress={(event) => {
                                                                if (!/[0-9]/.test(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <label>Mobile *</label>
                                                        {touched.mobile && errors.mobile && <div className="invalid-feedback text-start">{errors.mobile}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <Field name="streetAddress" className={`form-control ${touched.streetAddress && errors.streetAddress ? 'is-invalid' : ''}`} placeholder="Street Address" />
                                                        <label>Street Address *</label>
                                                        {touched.streetAddress && errors.streetAddress && <div className="invalid-feedback text-start">{errors.streetAddress}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <Field
                                                            name="city"
                                                            className={`form-control ${touched.city && errors.city ? 'is-invalid' : ''}`}
                                                            placeholder="City"
                                                            onKeyPress={(event) => {
                                                                if (!/[a-zA-Z\s]/.test(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <label>City *</label>
                                                        {touched.city && errors.city && <div className="invalid-feedback text-start">{errors.city}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <select
                                                            name="country"
                                                            className={`form-select ${touched.country && errors.country ? 'is-invalid' : ''}`}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                const countryCode = e.target.value;
                                                                setStates(State.getStatesOfCountry(countryCode));
                                                                setFieldValue('state', ''); // Reset state when country changes
                                                            }}
                                                            onBlur={handleBlur}
                                                            value={values.country}
                                                        >
                                                            <option value=""></option>
                                                            {countries.map((country) => (
                                                                <option key={country.isoCode} value={country.isoCode}>{country.name}</option>
                                                            ))}
                                                        </select>
                                                        <label>Country *</label>
                                                        {touched.country && errors.country && <div className="invalid-feedback text-start">{errors.country}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <select
                                                            name="state"
                                                            className={`form-select ${touched.state && errors.state ? 'is-invalid' : ''}`}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.state}
                                                            disabled={!values.country}
                                                        >
                                                            <option value=""></option>
                                                            {states.map((state) => (
                                                                <option key={state.isoCode} value={state.isoCode}>{state.name}</option>
                                                            ))}
                                                        </select>
                                                        <label>State *</label>
                                                        {touched.state && errors.state && <div className="invalid-feedback text-start">{errors.state}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating">
                                                        <Field
                                                            name="postalCode"
                                                            className={`form-control ${touched.postalCode && errors.postalCode ? 'is-invalid' : ''}`}
                                                            placeholder="Postal Code"
                                                            maxLength={6}
                                                            onKeyPress={(event) => {
                                                                if (!/[0-9]/.test(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <label>Postal Code *</label>
                                                        {touched.postalCode && errors.postalCode && <div className="invalid-feedback text-start">{errors.postalCode}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4 py-2 fw-medium"
                                                    style={{ backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)' }}
                                                >
                                                    {isEditMode ? 'Update' : 'Submit'}
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            )
            }


            <style jsx>{`
                .min-vh-75 {
                    min-height: 78vh;
                }
                .list-group-item.active {
                    background-color: var(--primary-color, #0d6efd) !important;
                    border-color: var(--primary-color, #0d6efd) !important;
                }
                .list-group-item {
                    border-left: none;
                    border-right: none;
                    border-radius: 0 !important;
                    transition: all 0.2s ease;
                }
                .list-group-item:first-child {
                    border-top: none;
                    border-left-width: 0;
                     border-right-width: 0;
                }
                .list-group-item:last-child {
                    border-bottom: none;
                     border-left-width: 0;
                     border-right-width: 0;
                }
                .list-group-item:hover:not(.active) {
                    background-color: #f8f9fa;
                    transform: translateX(5px);
                }
                .stat-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .stat-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15) !important;
                }
                @media (max-width: 768px) {
                    .sticky-sidebar {
                        position: relative !important;
                        top: 0 !important;
                    }
                }

                .profile-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #eef4ff; /* soft light background */
    display: flex;
    align-items: center;
    justify-content: center;
}

.form-floating > .form-select {
    border-radius: 8px !important;
    border: 1.5px solid #dee2e6 !important;
    padding-top: 1.625rem !important;
    padding-bottom: 0.625rem !important;
}

.form-floating > .form-select:focus {
    border-color: #86b7fe !important;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
}

.form-floating > .form-select.is-invalid {
    border-color: #dc3545 !important;
}

.form-floating > label {
    z-index: 3 !important;
}

.profile-icon i {
    font-size: 20px;
    color: var(--primary-color);
}
                
                /* Modal Styles */
                .modal.show {
                    display: block;
                    padding-right: 17px;
                }
                .form-floating > .form-control:focus,
                .form-floating > .form-select:focus {
                    border-color: var(--primary-color);
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25); /* Adjust based on primary color */
                }
                 .form-floating > label {
                    color: #6c757d;
                }
            `}</style>
        </>
    );
};

export default MyAccountPage;